import { ChevronRight, ExternalLink, MapPin } from 'lucide-react';
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";


import Scheduler from "../../scheduler.closest.jsx";
import { createOffer } from "../../../../../../services/staffingService";



import { ReactComponent as InfoCircleGreen } from "../../../../../_asset/img/info-circle_green.svg";
import { ReactComponent as TaskSquare } from "../../../../../_asset/img/task-square.svg";
import { ReactComponent as Star } from "../../../../../_asset/img/star-circle.svg";
import { ReactComponent as DollarCircle } from "../../../../../_asset/img/dollar-circle.svg";
import { ReactComponent as MakeOffer } from "../../../../../_asset/img/make_offer.svg";

// Custom Input Number Component to replace RSuite InputNumber
const CustomInputNumber = ({ value, onChange, size = "xs" }) => {
  return (
    <input
      type="number"
      value={value || ""}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      className="w-16 px-2 py-1 text-xs font-medium text-black bg-white border border-gray-300 rounded outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      min="0"
      step="0.01"
      placeholder="0"
    />
  );
};

// Custom Tag Component to replace RSuite Tag
const CustomTag = ({ children, className = "" }) => {
  return (
    <span className={`inline-block px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-50 rounded ${className}`}>
      {children}
    </span>
  );
};

// Custom Tooltip Component to replace RSuite Tooltip/Whisper
const CustomTooltip = ({ children, tooltip }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap z-10">
          {tooltip}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

// Utility functions for date formatting
function formatCompactShiftDate(dateString) {
  const options = { day: "2-digit", month: "short" };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

function formatFullShiftDate(dateString) {
  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

const ListingDetails = ({
  providerCompanyId,
  staffs,
  hoveredStaff,
  setStaffs,
  setHoveredStaff,
  job,
  setSelectedOfferId, 
  setActiveTab,
  setOfficersCount,
  officersCount ,
  fetchJobAndOfferMetadata
  
}) => {
  const [activeStaffId, setActiveStaffId] = useState(null);

  const handleMouseEnter = (staffMember) => {
    setHoveredStaff(staffMember);
  };

  const openModal = (staffId) => {
    setActiveStaffId(staffId);
  };

  const closeModal = () => {
    setActiveStaffId(null);
  };

  const sendOffer = async (staff, job) => {
    try {

      switch (true) {
        case staff.assignedShifts && staff.assignedShifts.length > 0:
          toast.info('Shifts cannot be assigned');
          return;
  
        case staff.availableShiftsForUnassignedStaff && staff.availableShiftsForUnassignedStaff.length > 0:
          staff.assignedShifts = [...staff.availableShiftsForUnassignedStaff];
          staff.availableShiftsForUnassignedStaff = [];
          // toast.success('Shifts moved to assigned successfully.');
          break;
  
        default:
          toast.warning('No available shifts to assign.');
          return;
      }
  
      // Proceed with sending the offer after mutation
      const resp = await createOffer(providerCompanyId, staff, job);
      setStaffs((prevStaffs) => {
        return prevStaffs.map((staff) => {
          if (staff._id === resp.data.offer.provider_resource_id) {
            return {
              ...staff,
              offer: {
                ...staff.offer,
                offerId: resp.data.offer._id,
                status: resp.data.offer.status,
              },
            };
          }
          return staff;
        });
      });
  
      toast.success('Offer sent successfully');
      setActiveStaffId(null);
  
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data);
      }
    }
  };
  

  const handleChange = (value, staffId) => {
    setStaffs((prevStaffs) =>
      prevStaffs.map((s) =>
        s._id === staffId ? { ...s, offerPrice: value } : s
      )
    );
  };

  const handleTabChange = (offerId) => {
    setSelectedOfferId(offerId);
    setActiveTab("2");
    fetchJobAndOfferMetadata();
    /*
    if (setSelectedOfferId && setActiveTab) {
    }
    */
  };

  // Update useEffect to set officers count when staffs change
  useEffect(() => {
    if (staffs && staffs.length) {
      setOfficersCount(staffs.length);
    }
  }, [staffs, setOfficersCount]);

  return (
    <div className="pb-8">
      {staffs.length > 0 && (
        <div className="grid grid-cols-1 gap-4 min-w-[600px] pr-3">
          {staffs.map((m) => (
            <div
              key={m._id}
              className="border border-gray-300 p-4 rounded-lg bg-white shadow-lg transition-shadow duration-200 hover:shadow-xl cursor-pointer"
              onClick={() => setHoveredStaff(m)}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="text-black text-base font-semibold">
                  {`${m.user_id?.firstName} ${m.user_id?.lastName}`}
                </div>
                <div className="px-2 py-1 rounded bg-gray-100">
                  <span className="text-black text-xs font-medium">
                    {`${(
                      ((job.hourlyPayRate - m.offerPrice) / job.hourlyPayRate) *
                      100
                    ).toFixed(0)}% Margin`}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-black text-sm font-normal mb-4 mt-2">
                <MapPin className="!w-4 !h-4 text-black" />
                {`${m.formattedLocation} (${m.distanceMiles?.toFixed(
                  2
                )} miles away)`}
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-black text-xs font-medium">
                  <DollarCircle />
                  {`20 jobs done in last 3 months`}
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-black text-xs font-medium">
                  <Star />
                  {`$${m.preferences?.minHourlyRate} per hour `}
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-black text-xs font-medium">
                  <TaskSquare />
                  4.3
                </div>
              </div>

              {/* {m.assignedShifts && m.assignedShifts.length > 0 && (
                <div className="assigned-shifts">
                  <span className="label"> Available Shifts:</span>
                  <TagGroup>
                    {m.assignedShifts.map(shift => (
                      <Whisper
                        key={shift._id}
                        placement="top"
                        trigger="hover"
                        speaker={
                          <Tooltip>
                            {`${formatFullShiftDate(shift.startTime)} Hrs - ${formatFullShiftDate(shift.endTime)} Hrs`}
                          </Tooltip>
                        }
                      >
                        <Tag>
                          {formatCompactShiftDate(shift.date)}
                        </Tag>
                      </Whisper>
                    ))}
                  </TagGroup>
                </div>
              )} */}

              <>
                {/* Show Offered Shifts if staff has an existing offer */}
                {m.offer ? (
                  <div className="mb-4">
                    <span className="text-xs font-medium text-gray-600 mb-2 block">Offered Shifts:</span>
                    <div className="flex flex-wrap gap-1">
                      {m.assignedShifts.map((shift) => (
                        <CustomTooltip
                          key={shift._id}
                          tooltip={`${formatFullShiftDate(
                            shift.startTime
                          )} Hrs - ${formatFullShiftDate(
                            shift.endTime
                          )} Hrs`}
                        >
                          <CustomTag>{formatCompactShiftDate(shift.date)}</CustomTag>
                        </CustomTooltip>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Show Available Shifts if no offer exists */
                  m.availableShiftsForUnassignedStaff &&
                  m.availableShiftsForUnassignedStaff.length > 0 && (
                    <div className="mb-4">
                      <span className="text-xs font-medium text-gray-600 mb-2 block">Available Shifts:</span>
                      <div className="flex flex-wrap gap-1">
                        {m.availableShiftsForUnassignedStaff.map((shift) => (
                          <CustomTooltip
                            key={shift._id}
                            tooltip={`${formatFullShiftDate(
                              shift.startTime
                            )} Hrs - ${formatFullShiftDate(
                              shift.endTime
                            )} Hrs`}
                          >
                            <CustomTag>{formatCompactShiftDate(shift.date)}</CustomTag>
                          </CustomTooltip>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </>

              <hr className="border-gray-200 my-4" />

              {!m.offer && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Set Hourly Rate:</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-black mr-1">$</span>
                      <CustomInputNumber
                        value={m.offerPrice}
                        onChange={(value) => handleChange(value, m._id)}
                      />
                    </div>
                  </div>
                  {activeStaffId === m._id && (
                    <Scheduler
                      isOpen={true}
                      onRequestClose={closeModal}
                      className="scheduler-modal"
                      overlayClassName="scheduler-modal-overlay"
                      ariaHideApp={false}
                      sendOffer={sendOffer}
                      staff={m}
                      job={job}
                    />
                  )}
                  {
                    m.availableShiftsForUnassignedStaff?.length > 0 && (
                    <button
                      onClick={() => openModal(m._id)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                    >
                      Make Offer <MakeOffer />
                    </button>
                    )
                  }
                </div>
              )}

              {m.offer?.offerId && (
                <div className="flex justify-end">
                  <div
                    className="flex items-center gap-1 font-medium cursor-pointer text-sm transition-colors hover:opacity-80"
                    onClick={() => {
                      handleTabChange(m.offer.offerId);
                    }}
                  >
                    {m.offer.status === "completed" && (
                      <span className="text-green-600">
                        Pending Payment <ChevronRight className="!text-base" />
                      </span>
                    )}
                    {m.offer.status === "paid" && (
                      <span className="text-green-600">
                        Payment Completed <ChevronRight className="!text-base" />
                      </span>
                    )}
                    {m.offer.status === "accepted" && (
                      <span className="text-green-600">
                        Offer Accepted <ChevronRight className="!text-base" />
                      </span>
                    )}
                    {m.offer.status === "rejected" && (
                      <span className="text-red-600">
                        Offer Rejected <ChevronRight className="!text-base" />
                      </span>
                    )}
                    {m.offer.status === "expired" && (
                      <span className="text-red-600">
                        Offer Expired <ChevronRight className="!text-base" />
                      </span>
                    )}
                    {m.offer.status === "open" && (
                      <span className="text-yellow-500 text-xs">
                        Pending Response{" "}
                        <ExternalLink className="!text-sm" />
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListingDetails;
